import { useEffect, useState } from "react";
import { Search, X, Plane, Building2, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { airlineLogo } from "../../lib/assets";

export default function AddFavoriteModal({ type, onClose, onAdded }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const isAirport = type === "airport";

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      const path = isAirport ? "/airports" : "/airlines";
      api.get(path, { params: { q, limit: 12 } }).then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, isAirport]);

  const add = async (it) => {
    try {
      const r = await api.post("/travel/favorites", { entity_type: type, entity_id: it.id });
      const hint = r.data?.loyalty;
      if (hint) {
        toast.success("Added to favorites", {
          description: `${it.name} has ${hint.program} — track your miles in My Loyalty & Mileage.`,
        });
      } else {
        toast.success("Added to favorites");
      }
      onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 pt-20" onClick={onClose} data-testid="add-favorite-modal">
      <div className="bg-white w-full max-w-lg border border-zinc-300 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <h3 className="font-heading font-900 text-lg flex items-center gap-2">
            {isAirport ? <Building2 className="w-5 h-5 text-primary" /> : <Plane className="w-5 h-5 text-primary -rotate-45" />}
            Add favorite {isAirport ? "airport" : "airline"}
          </h3>
          <button onClick={onClose} data-testid="add-favorite-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus data-testid="add-favorite-search"
              placeholder={isAirport ? "Search by name, city or code (e.g. SFO)" : "Search by name or code (e.g. UA)"}
              className="w-full border border-zinc-300 pl-9 pr-3 py-2.5 outline-none focus:ring-2 focus:ring-primary text-base" />
          </div>
          <div className="max-h-72 overflow-auto divide-y divide-zinc-100">
            {loading ? <div className="py-6 text-center text-sm text-muted-foreground">Searching…</div>
              : items.length === 0 ? <div className="py-6 text-center text-sm text-muted-foreground">No results.</div>
                : items.map((it) => (
                  <div key={it.id} className="flex items-center gap-3 py-2.5" data-testid={`add-fav-result-${it.id}`}>
                    {isAirport
                      ? <span className="font-mono text-sm text-primary w-12 shrink-0">{it.iata_code || it.icao_code || "—"}</span>
                      : (airlineLogo(it.iata)
                        ? <img src={airlineLogo(it.iata)} alt="" className="w-8 h-8 object-contain shrink-0" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                        : <span className="font-mono text-sm text-primary w-8 shrink-0">{it.iata || "—"}</span>)}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{it.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{isAirport ? `${it.municipality || "—"}, ${it.iso_country}` : (it.country || "—")}</div>
                    </div>
                    <button onClick={() => add(it)} data-testid={`add-fav-btn-${it.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-white text-xs font-600 hover:bg-blue-700 transition-colors">
                      <Plus className="w-3.5 h-3.5" /> Add
                    </button>
                  </div>
                ))}
          </div>
        </div>
      </div>
    </div>
  );
}
